
import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Modal from '@mui/material/Modal';
import AddIcon from '@mui/icons-material/Add';
import { Autocomplete, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { Form, Formik, getIn } from 'formik';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { ErrorAlert, SuccessAlert } from '../../utils/Alerts';


import * as Yup from "yup"
import { LoadingButton } from '@mui/lab';
import { useCreateUserMutation } from '../../Slices/roomNateApis';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    height: "90%",
    overflowY: "auto",
    width: { xs: '90%', md: "650px" },
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

export default function BasicModal() {
    const [open, setOpen] = React.useState(false);
    const [load, setLoad] = React.useState(false)
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState(false);

    const [createUser, resultStats] = useCreateUserMutation()

    React.useEffect(() => {
        if (resultStats?.isLoading) {
            setLoad(true)
        }

        if (!resultStats?.isLoading) {
            setLoad(false)
        }

        if (resultStats?.isSuccess) {
            setSuccessAlert(true)
            setTimeout(() => {
                setSuccessAlert(false)
            }, 2000);
            handleClose()
        }

        if (resultStats?.isError) {
            setErrorAlert(true)
            setTimeout(() => {
                setErrorAlert(false)
            }, 2000);

        }

    }, [resultStats])


    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("No name provided"),
        email: Yup.string().required("No email provided")
            .matches("^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:.[a-zA-Z0-9-]+)*$", "No correct email has been provided")
        ,
        password: Yup.string().required("No password provided.")
            .min(8, 'Password is too short - should be 8 chars minimum.')
        ,
        confirmPassword: Yup.string().required("No confirm password provided.")
            .oneOf([Yup.ref('password'), null], 'Passwords must match')
    });

    return (
        <>
            {successAlert &&
                <SuccessAlert title="User has been created successfully!" />
            }

            {errorAlert &&
                <ErrorAlert title="Something Went Wrong!" />
            }

            <Button onClick={handleOpen} variant="outlined" endIcon={<AddIcon />}>Add User</Button>
            <Modal
                open={open}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    <Box position="fixed" top={10} right={0}>
                        <IconButton onClick={handleClose}>
                            <Close />
                        </IconButton>
                    </Box>

                    <Formik
                        initialValues={{
                            name: "",
                            email: "",
                            password: "",
                            confirmPassword: "",
                            role: "user"
                        }}
                        validationSchema={validationSchema}
                        onSubmit={(values) => {
                            let data;
                            const { email, name, role, password } = values;
                            data = { email, name, role, password }
                            createUser(data)
                        }}
                    >
                        {({ values, touched, errors, handleChange, handleBlur, isValid, setValues }) => (
                            <Form noValidate autoComplete="off" >
                                <Typography id="modal-modal-title" variant='h6' mb={2}>
                                    Create a User
                                </Typography>

                                <Stack my={2}>
                                    <TextField id="outlined-basic" label="Name" variant="outlined"
                                        name='name'
                                        fullWidth
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.name}
                                        required
                                        helperText={
                                            getIn(touched, "name") && getIn(errors, "name")
                                                ? getIn(errors, "name")
                                                : ""
                                        }
                                        error={Boolean(getIn(touched, "name") && getIn(errors, "name"))}
                                    />
                                </Stack>

                                <Stack my={2}>
                                    <TextField id="outlined-basic" label="Email" variant="outlined"
                                        name='email'
                                        fullWidth
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.email}
                                        required
                                        type={"email"}
                                        helperText={
                                            getIn(touched, "email") && getIn(errors, "email")
                                                ? getIn(errors, "email")
                                                : ""
                                        }
                                        error={Boolean(getIn(touched, "email") && getIn(errors, "email"))}
                                    />
                                </Stack>

                                <Stack my={2}>
                                    <TextField
                                        required
                                        fullWidth
                                        name="password"
                                        label="Password"
                                        type={values?.pswShow ? "text" : "password"}
                                        id="password"
                                        onChange={handleChange}
                                        helperText={
                                            getIn(touched, "password") && getIn(errors, "password")
                                                ? getIn(errors, "password")
                                                : ""
                                        }
                                        error={Boolean(getIn(touched, "password") && getIn(errors, "password"))}
                                        onBlur={handleBlur}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end" onClick={(e) => setValues({ ...values, pswShow: !values?.pswShow })} sx={{ cursor: "pointer" }}>
                                                    {values?.pswShow ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Stack>

                                <Stack my={2}>
                                    <TextField
                                        required
                                        fullWidth
                                        name="confirmPassword"
                                        label="Confirm Password"
                                        type={values?.confirmPswShow ? "text" : "password"}
                                        id="confirmPassword"
                                        onChange={handleChange}
                                        helperText={
                                            getIn(touched, "confirmPassword") && getIn(errors, "confirmPassword")
                                                ? getIn(errors, "confirmPassword")
                                                : ""
                                        }
                                        error={Boolean(getIn(touched, "confirmPassword") && getIn(errors, "confirmPassword"))}
                                        onBlur={handleBlur}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end" onClick={(e) => setValues({ ...values, confirmPswShow: !values?.confirmPswShow })} sx={{ cursor: "pointer" }}>
                                                    {values?.confirmPswShow ? <VisibilityIcon /> : <VisibilityOffIcon />}
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Stack>

                                <Stack my={2}>
                                    <Autocomplete
                                        name='package_id'
                                        fullWidth
                                        disablePortal
                                        id="combo-box-demo"
                                        onChange={(e, value) => setValues({ ...values, package_id: value })}
                                        options={top100Films}
                                        renderInput={(params) => <TextField {...params} label="Package" name="package_id" onBlur={handleBlur} helperText={
                                            getIn(touched, "package_id") && getIn(errors, "package_id")
                                                ? getIn(errors, "package_id")
                                                : ""
                                        }
                                            error={Boolean(getIn(touched, "package_id") && getIn(errors, "package_id"))} />}
                                    />
                                </Stack>

                                <Stack mt={2}>
                                    <LoadingButton loading={load} variant="contained" fullWidth type="submit">
                                        Add
                                    </LoadingButton>
                                </Stack>
                            </Form>
                        )}
                    </Formik>


                </Box>
            </Modal >
        </>
    );
}

const top100Films = [
    { label: 'Full furnished package', package_id: 1 },
];